import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n } from '../../helpers/i18n';
import { resetPassword, logInMvp, fetchUserMvp } from 'redux/actions/user.action';
import warning from 'static/images/mvp/warning.png';
import { toast } from 'react-toastify';
import utils from 'helpers/utils';
import './signin.scss';
import {
  Button,
  FormField,
  FormSection,
  Input,
  InputLabel,
  SectionBody,
  SectionFooter,
  SectionHeader,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import { formTheme } from './ThemeSignIn';

const ResetPassword = ({ resetPassword, logInMvp, fetchUserMvp, loading, location, history, header }) => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    const tokenParam = urlParams.get('token');
    
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
    if (tokenParam) {
      setToken(tokenParam);
    }
    
    // If no email or token, redirect to forgot password
    if (!emailParam && !tokenParam) {
      history.push('/forgot-password');
    }
  }, [location.search, history]);

  const handleNewPasswordChange = (value) => {
    setNewPassword(value);
    // Clear error when user starts typing again
    setError('');
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    // Clear error when user starts typing again
    setError('');
  };

  const handleResetPassword = async () => {
    // Only allow if both passwords are filled
    if (!newPassword || !confirmPassword) {
      setError(I18n.t('text_field_required_error'));
      return;
    }

    // Check if passwords match before proceeding
    if (newPassword !== confirmPassword) {
      setError(I18n.t('forgot_password_passwords_not_match'));
      return;
    }

    // Clear any previous errors
    setError('');

    try {
      await resetPassword(email, newPassword, confirmPassword, token);
      
      // Auto login with new password
      try {
        const loginResult = await logInMvp(email, newPassword);
        if (loginResult.status === 'OK') {
          // Save user info to localStorage (same as SignIn.js)
          let dataUser = {
            type: loginResult.user.type,
            mvpUserType: loginResult.user.mvpUserType,
            name: loginResult.user.name,
            _id: loginResult.user._id,
            accessToken: loginResult.accessToken,
            refreshToken: loginResult.refreshToken,
          };
          // Save user info to localStorage
          localStorage.setItem('ced:user-info', JSON.stringify(dataUser));
          // Save refresh token to cookie
          if (loginResult.refreshToken) {
            utils.saveToCookie('_RFT', loginResult.refreshToken, 365);
          }
          // Fetch full user info and update Redux store
          await fetchUserMvp();
          
          // Show success toast
          toast.success(I18n.t('forgot_password_success_and_logged_in'), {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            closeButton: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            className: 'toast-success-container',
            theme: 'colored',
          });
          setTimeout(() => {
            history.push('/'); // Redirect to home page after successful login
          }, 2000);
        } else {
          // If auto login fails, show success toast and redirect to login
          toast.success(I18n.t('forgot_password_success'), {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            closeButton: false,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            className: 'toast-success-container',
            theme: 'colored',
          });
          setTimeout(() => {
            history.push('/login');
          }, 2000);
        }
      } catch (loginErr) {
        console.error('Auto login failed:', loginErr);
        // If auto login fails, show success toast and redirect to login
        toast.success(I18n.t('forgot_password_success'), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          closeButton: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          className: 'toast-success-container',
          theme: 'colored',
        });
        setTimeout(() => {
          history.push('/login');
        }, 2000);
      }
    } catch (err) {
      if (err.message.includes('Invalid or expired reset token')) {
        setError(I18n.t('reset_token_expired'));
      } else {
        setError(err.message || I18n.t('something_wrong'));
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Only allow Enter key submission if both passwords are filled
      if (newPassword && confirmPassword && !loading) {
        handleResetPassword();
      }
    }
  };

  const theme = {
    input: {
      color: '#000000',
      fontSize: '16px',
      lineHeight: '24px',
      borderColor: error ? '#FF445D' : '#CCCCCC',
      borderRadius: '4px',
      padding: '14px 16px',
    },
  };

  return (
    <div id="site_wrapper">
      {header}
      <FormSection theme={formTheme}>
        <SectionHeader theme={formTheme}>{I18n.t('forgot_password_reset_title')}</SectionHeader>
        <SectionBody style={{ marginBottom: 24 }}>
          {/* <div className="content" style={{ opacity: 0.7, fontSize: '16px', marginBottom: '25px', lineHeight: '1.5' }}>
            <span>{I18n.t('forgot_password_reset_description')}</span>
            {email && (
              <div className="email-info" style={{ marginTop: '10px', fontSize: '14px', color: '#01ECC3' }}>
                <strong>Email:</strong> {email}
              </div>
            )}
          </div> */}
          
          <FormField>
            <InputLabel theme={formTheme}>{I18n.t('forgot_password_new_password_placeholder')} <span className="text-danger">*</span></InputLabel>
            <Input
              placeholder={I18n.t('forgot_password_new_password_placeholder')}
              key="newPassword"
              name="newPassword"
              type="password"
              value={newPassword}
              theme={theme}
              onChange={(e) => handleNewPasswordChange(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
          </FormField>

          <FormField>
            <InputLabel theme={formTheme}>{I18n.t('forgot_password_confirm_password_placeholder')} <span className="text-danger">*</span></InputLabel>
            <Input
              placeholder={I18n.t('forgot_password_confirm_password_placeholder')}
              key="confirmPassword"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              theme={theme}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            {error && (
              <span className="error-message">
                <img src={warning} style={{ marginTop: '-5px', marginRight: '4px' }} alt="Warning" />
                {error}
              </span>
            )}
          </FormField>
        </SectionBody>
        <SectionFooter theme={formTheme}>
          <Button 
            theme={formTheme} 
            onClick={handleResetPassword} 
            disabled={loading || !newPassword || !confirmPassword}
            style={{ 
              width: '100%',
              opacity: (newPassword && confirmPassword && !loading) ? 1 : 0.6,
              cursor: (newPassword && confirmPassword && !loading) ? 'pointer' : 'not-allowed',
              backgroundColor: (!newPassword || !confirmPassword || loading) ? '#CCCCCC' : undefined,
              color: (!newPassword || !confirmPassword || loading) ? '#666666' : undefined
            }}
          >
            {loading ? I18n.t('loading') : I18n.t('forgot_password_update_password')}
          </Button>
        </SectionFooter>
      </FormSection>
    </div>
  );
};

const mapStateToProps = ({ user }) => ({
  loading: user.loading,
});

const mapDispatchToProps = {
  resetPassword,
  logInMvp,
  fetchUserMvp,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ResetPassword));
